import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart3, TrendingUp, Users, DollarSign, Target, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const FUNNEL_COLORS = [
  "#6366F1", "#8B5CF6", "#F59E0B", "#F97316", "#3B82F6", "#EC4899", "#10B981", "#EF4444"
];

function KPICard({ label, value, sub, icon: Icon, color }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "16px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: "12px", right: "14px", width: "32px", height: "32px", borderRadius: "8px", background: `${color}12`, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div style={{ fontSize: "10px", fontWeight: "700", textTransform: "uppercase", color: "#8AA496", letterSpacing: "0.05em", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontSize: "24px", fontWeight: "800", color: "#1B2B22", fontFamily: "Outfit, sans-serif" }}>{value}</div>
      {sub && <div style={{ fontSize: "11px", color: "#64748B", marginTop: "2px" }}>{sub}</div>}
    </div>
  );
}

function FunnelBar({ label, count, maxCount, color, index }) {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
      <div style={{ width: "130px", flexShrink: 0, fontSize: "11px", fontWeight: "600", color: "#1B2B22", textAlign: "right" }}>{label}</div>
      <div style={{ flex: 1, height: "24px", background: "#F1F5F9", borderRadius: "6px", position: "relative", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: "6px", background: color, width: `${Math.max(pct, 2)}%`, transition: "width 0.6s ease", display: "flex", alignItems: "center", paddingLeft: "8px" }}>
          {pct > 15 && <span style={{ fontSize: "10px", fontWeight: "700", color: "#fff" }}>{count}</span>}
        </div>
        {pct <= 15 && count > 0 && <span style={{ position: "absolute", left: `${Math.max(pct, 3)}%`, top: "50%", transform: "translateY(-50%)", marginLeft: "6px", fontSize: "10px", fontWeight: "700", color: "#64748B" }}>{count}</span>}
      </div>
    </div>
  );
}

function SourceTable({ sourceStats }) {
  const entries = Object.entries(sourceStats).sort((a, b) => b[1].total - a[1].total);
  if (entries.length === 0) return <div style={{ fontSize: "12px", color: "#94A3B8", textAlign: "center", padding: "20px" }}>No lead source data yet</div>;

  return (
    <table style={{ width: "100%", fontSize: "12px", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #E2E8F0" }}>
          <th style={{ textAlign: "left", padding: "8px 10px", fontSize: "10px", fontWeight: "700", color: "#64748B", textTransform: "uppercase" }}>Source</th>
          <th style={{ textAlign: "center", padding: "8px 10px", fontSize: "10px", fontWeight: "700", color: "#64748B", textTransform: "uppercase" }}>Total</th>
          <th style={{ textAlign: "center", padding: "8px 10px", fontSize: "10px", fontWeight: "700", color: "#64748B", textTransform: "uppercase" }}>Won</th>
          <th style={{ textAlign: "center", padding: "8px 10px", fontSize: "10px", fontWeight: "700", color: "#64748B", textTransform: "uppercase" }}>Conv. %</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([src, stats]) => (
          <tr key={src} style={{ borderBottom: "1px solid #F1F5F9" }}>
            <td style={{ padding: "8px 10px", fontWeight: "600", color: "#1B2B22" }}>{src}</td>
            <td style={{ padding: "8px 10px", textAlign: "center", color: "#475569" }}>{stats.total}</td>
            <td style={{ padding: "8px 10px", textAlign: "center", color: "#059669", fontWeight: "600" }}>{stats.won}</td>
            <td style={{ padding: "8px 10px", textAlign: "center", color: "#64748B" }}>{stats.total > 0 ? Math.round(stats.won / stats.total * 100) : 0}%</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RepLeaderboard({ repStats }) {
  const entries = Object.entries(repStats).sort((a, b) => b[1].revenue - a[1].revenue);
  if (entries.length === 0) return <div style={{ fontSize: "12px", color: "#94A3B8", textAlign: "center", padding: "20px" }}>No rep data yet</div>;

  return (
    <div>
      {entries.map(([name, stats], i) => (
        <div key={name} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 0", borderBottom: i < entries.length - 1 ? "1px solid #F1F5F9" : "none" }}>
          <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: i === 0 ? "#FFB703" : i === 1 ? "#94A3B8" : "#D1D5DB", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: "800", color: i === 0 ? "#1B2B22" : "#fff", flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "12px", fontWeight: "700", color: "#1B2B22", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
            <div style={{ fontSize: "10px", color: "#64748B" }}>{stats.assigned} assigned · {stats.won} won</div>
          </div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: "#2F7D52" }}>
            {stats.revenue > 0 ? `₹${(stats.revenue / 100000).toFixed(1)}L` : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CRMReportsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/crm/reports`);
      setReport(data);
    } catch (err) { console.error(err); toast.error("Failed to load CRM reports"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) return <div style={{ padding: "24px", color: "#94A3B8" }}>Loading reports...</div>;
  if (!report) return <div style={{ padding: "24px", color: "#94A3B8" }}>No data available</div>;

  const funnelEntries = Object.entries(report.funnel || {});
  const maxFunnel = Math.max(1, ...funnelEntries.map(([, v]) => v.count));

  return (
    <div style={{ padding: "20px 24px", fontFamily: "Manrope, sans-serif" }} data-testid="crm-reports-page">
      <div style={{ marginBottom: "20px" }}>
        <h1 style={{ fontSize: "22px", fontWeight: "800", color: "#1B2B22", margin: 0 }}>CRM Analytics</h1>
        <p style={{ fontSize: "12px", color: "#64748B", marginTop: "2px" }}>Pipeline performance and lead insights</p>
      </div>

      {/* KPI Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px", marginBottom: "20px" }}>
        <KPICard label="Total Leads" value={report.total_leads} sub={`${report.total_this_month} this month`} icon={Users} color="#6366F1" />
        <KPICard label="Pipeline Value" value={`₹${(report.pipeline_value / 100000).toFixed(1)}L`} sub={`${report.active_count} active deals`} icon={DollarSign} color="#2F7D52" />
        <KPICard label="Conversion Rate" value={`${report.conversion_rate}%`} sub={`${report.won_count} won / ${report.won_count + report.lost_count} closed`} icon={Target} color="#F59E0B" />
        <KPICard label="Avg Deal Size" value={report.avg_deal_size > 0 ? `₹${(report.avg_deal_size / 100000).toFixed(1)}L` : "—"} sub="Won deals average" icon={TrendingUp} color="#3B82F6" />
        <KPICard label="Revenue Forecast" value={report.revenue_forecast > 0 ? `₹${(report.revenue_forecast / 100000).toFixed(1)}L` : "—"} sub="Weighted by stage probability" icon={BarChart3} color="#EC4899" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        {/* Funnel */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "18px" }}>
          <div style={{ fontSize: "13px", fontWeight: "800", color: "#1B2B22", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.03em" }}>Pipeline Funnel</div>
          {funnelEntries.map(([key, val], i) => (
            <FunnelBar key={key} label={val.label} count={val.count} maxCount={maxFunnel} color={FUNNEL_COLORS[i] || "#94A3B8"} index={i} />
          ))}
        </div>

        {/* Lead Sources */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "18px" }}>
          <div style={{ fontSize: "13px", fontWeight: "800", color: "#1B2B22", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.03em" }}>Lead Sources</div>
          <SourceTable sourceStats={report.source_stats || {}} />
        </div>

        {/* Rep Leaderboard */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "18px" }}>
          <div style={{ fontSize: "13px", fontWeight: "800", color: "#1B2B22", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.03em" }}>Sales Leaderboard</div>
          <RepLeaderboard repStats={report.rep_leaderboard || {}} />
        </div>

        {/* Quick Stats */}
        <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "18px" }}>
          <div style={{ fontSize: "13px", fontWeight: "800", color: "#1B2B22", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.03em" }}>Overview</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {[
              { label: "Won", value: report.won_count, color: "#10B981", icon: ArrowUpRight },
              { label: "Lost", value: report.lost_count, color: "#EF4444", icon: ArrowDownRight },
              { label: "Active", value: report.active_count, color: "#3B82F6", icon: TrendingUp },
              { label: "This Month", value: report.total_this_month, color: "#8B5CF6", icon: Target },
            ].map(item => (
              <div key={item.label} style={{ background: "#F8FAFC", borderRadius: "8px", padding: "12px", display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "7px", background: `${item.color}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <item.icon size={14} style={{ color: item.color }} />
                </div>
                <div>
                  <div style={{ fontSize: "18px", fontWeight: "800", color: "#1B2B22" }}>{item.value}</div>
                  <div style={{ fontSize: "10px", color: "#64748B" }}>{item.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
